import React, { useRef, useState } from 'react';
import {
  ActionIcon, Box, Button, Group, Image, Progress, Switch, Text, TextInput, Tooltip,
} from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { canUploadImageInCurrentContext, uploadWebImage, uploadWebVideo } from '../../requests/webMedia';
import { detectEmbedProvider, getEmbedThumbnail } from '../../media/embedProviders';
import { getMediaBankSource, invalidateMediaBank } from '../../utils/mediaBankRegistry';
import { getWebLinkSource } from '../../utils/webLinkRegistry';
import { rememberBankMedia } from '../../utils/useBankMedia';
import { MEDIA_TYPES } from '../../../shared/media/mediaBank';
import {
  MAX_MEDIA_SIZE, MAX_MEDIA_SIZE_LABEL, PROGRESS_THRESHOLD,
} from '../../../shared/media/mediaLimits';
import {
  AUTO_RESOLUTION,
  MEDIA_CONTEXTS,
  defaultResolutionFor,
  mediaSelection,
  resolveMediaSrcset,
  resolveMediaUrl,
} from '../../../shared/media/mediaResolution';
import MediaPreviewModal from '../../media/MediaPreviewModal';
import InspectorRow from './InspectorRow';
import MediaBankPicker from './MediaBankPicker';
import MediaResolutionField from './MediaResolutionField';
import VariableImagePicker, { canPickVariableImage } from './VariableImagePicker';
import { isWebTemplateCatalog } from '../../utils/variableCatalogRegistry';
import { isVariableBox } from '../../../shared/variables/variableBoxes';
import ImageFocalPointPicker from './ImageFocalPointPicker';

const FALLBACK_PREVIEW = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='110'%3E%3Crect width='100%25' height='100%25' fill='%23f1f3f5'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%236c757d' font-size='12'%3ELien invalide%3C/text%3E%3C/svg%3E";

/** Config par type de média — tout le reste du contrôle est identique. */
const KINDS = {
  image: {
    accept: 'image/png,image/jpeg,image/jpg,image/gif,image/webp',
    upload: uploadWebImage,
    icon: icon({ name: 'cloud-arrow-up', family: 'duotone', style: 'solid' }),
    addLabel: 'Ajouter une image',
    hint: `Cliquez ou glissez-déposez — ${MAX_MEDIA_SIZE_LABEL} max`,
    urlPlaceholder: 'https://exemple.com/image.jpg',
    typeError: 'Ce fichier n\'est pas une image. Formats acceptés : PNG, JPG, GIF, WebP.',
    sizeError: `Image trop lourde (${MAX_MEDIA_SIZE_LABEL} maximum). Réduisez-la puis réessayez.`,
    removeLabel: 'Retirer l\'image',
    previewLabel: 'Prévisualiser l\'image',
    dropAria: 'Ajouter une image : cliquez pour parcourir ou glissez-déposez',
    /* La banque sert aussi les GIF à un champ image : c'est une image animée,
       pas une vidéo, et elle se pose exactement au même endroit. */
    bankTypes: [MEDIA_TYPES.image, MEDIA_TYPES.gif],
  },
  video: {
    /* MP4 et WebM seulement : ce sont les deux formats que les navigateurs
       lisent tous. Un .mov ou un .ogg partait au stockage puis ne s'affichait
       nulle part — un fichier accepté qui ne se lit pas est pire qu'un refus. */
    accept: 'video/mp4,video/webm',
    upload: uploadWebVideo,
    icon: icon({ name: 'video', family: 'duotone', style: 'solid' }),
    addLabel: 'Ajouter une vidéo',
    hint: `Cliquez ou glissez-déposez — ${MAX_MEDIA_SIZE_LABEL} max`,
    urlPlaceholder: 'https://youtube.com/… ou fichier .mp4',
    typeError: 'Ce format de vidéo n\'est pas lisible partout. Formats acceptés : MP4, WebM.',
    sizeError: `Vidéo trop lourde (${MAX_MEDIA_SIZE_LABEL} maximum).`,
    removeLabel: 'Retirer la vidéo',
    previewLabel: 'Prévisualiser la vidéo',
    dropAria: 'Ajouter une vidéo : cliquez pour parcourir ou glissez-déposez',
    bankTypes: [MEDIA_TYPES.video],
  },
};

/** Média posé à la main (lien collé) : plus de palier ni d'id à suivre. */
const NO_BANK_MEDIA = { mediaId: null, resolution: AUTO_RESOLUTION, srcset: '' };

function MediaPreview({ kind, value }) {
  if (kind === 'video') {
    const provider = detectEmbedProvider(value);
    if (provider) {
      return (
        <Image
          src={getEmbedThumbnail(provider, value) || FALLBACK_PREVIEW}
          alt="Aperçu de la vidéo"
          h={110}
          fit="contain"
          fallbackSrc={FALLBACK_PREVIEW}
        />
      );
    }
    return (
      <video
        src={value}
        muted
        playsInline
        preload="metadata"
        style={{ width: '100%', height: 110, objectFit: 'contain', display: 'block', background: '#000' }}
      />
    );
  }
  return (
    <Image
      src={value}
      alt="Aperçu de l'image"
      h={110}
      fit="contain"
      fallbackSrc={FALLBACK_PREVIEW}
    />
  );
}

/**
 * Champ média unifié (image OU vidéo) — deux états :
 * - vide : une seule surface (clic = parcourir, drop = upload), le collage
 *   d'un lien est replié derrière « Voir le lien » ;
 * - rempli : aperçu + Remplacer / Voir le lien / ✕ (drop sur l'aperçu
 *   remplace aussi).
 * Source unique de vérité : `value` (string URL) — upload, banque et lien
 * écrivent au même endroit, la dernière action gagne.
 *
 * `meta` porte ce qu'une URL seule ne dit pas : l'id du média de la banque,
 * le palier de résolution retenu et le `srcset` quand il y en a un. L'appelant
 * qui le suit reçoit tout d'un coup par `onMediaChange` et décide où le ranger
 * (props voisines du bloc, ou objet média) ; sans `onMediaChange`, le champ
 * écrit l'URL par `onChange` et n'offre pas de palier, comme avant.
 *
 * `form` / `onFormChange` : réglages de formulaire de l'image (`XForm`, voir
 * `imageSlots`). Dans un modèle, le champ y règle « Afficher dans le
 * formulaire », comme le HideInFormSwitcher de la box image du classique.
 */
const MediaUploadField = ({
  value, onChange, fieldLabel, kind = 'image', meta, onMediaChange, form, onFormChange,
  focalPoint, onFocalPointChange,
}) => {
  const cfg = KINDS[kind] || KINDS.image;
  const inputRef = useRef(null);
  const focalButtonRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [percent, setPercent] = useState(null);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [showBank, setShowBank] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editingFocalSrc, setEditingFocalSrc] = useState(null);

  const canUpload = canUploadImageInCurrentContext();
  /* La banque n'existe que si un document la borne : le bouton disparaît
     plutôt que d'ouvrir un panneau qui ne peut rien afficher. */
  const canPickFromBank = !!getMediaBankSource();
  /* La banque de liens est servie par le proposal lui-même, la banque de médias
     par le document d'origine : l'une peut exister sans l'autre. */
  const canPickLinks = !!getWebLinkSource();
  const canOpenBank = canPickFromBank || canPickLinks;
  /* Une seule écriture quand l'appelant suit les paliers : `onChange` et une
     seconde écriture des props voisines partiraient du même état figé, et la
     deuxième effacerait la première. */
  const tracksMeta = typeof onMediaChange === 'function';
  const commit = (next) => {
    if (tracksMeta) onMediaChange(next);
    else onChange(next.url);
  };

  const toggleUrlInput = () => {
    setShowUrlInput((v) => !v);
    setShowBank(false);
  };
  const toggleBank = () => {
    setShowBank((v) => !v);
    setShowUrlInput(false);
  };

  const applyMedia = (media, resolution) => commit({
    ...mediaSelection(media, resolution),
    ...(kind === 'video' ? { poster: media.posterUrl || '' } : {}),
  });

  const selectFromBank = (media) => {
    rememberBankMedia(media);
    applyMedia(media, defaultResolutionFor(media, MEDIA_CONTEXTS.web));
    setShowBank(false);
    setError('');
  };

  const setUrlByHand = (url) => commit({ url, ...NO_BANK_MEDIA });

  const changeResolution = (resolution, media) => commit({
    url: resolveMediaUrl(media, resolution),
    mediaId: media.id,
    resolution,
    srcset: resolveMediaSrcset(media, resolution),
  });

  const processFile = async (file) => {
    if (!file) return;

    /* La liste `accept` de l'explorateur de fichiers n'est qu'une suggestion :
       un glisser-déposer, ou un « tous les fichiers », la contourne. */
    if (!cfg.accept.split(',').includes(file.type)) {
      setError(cfg.typeError);
      return;
    }
    if (file.size > MAX_MEDIA_SIZE) {
      setError(cfg.sizeError);
      return;
    }

    setError('');
    setUploading(true);
    /* En dessous de quelques mégaoctets la barre n'aurait le temps de rien
       montrer ; au-dessus, son absence fait passer l'envoi pour un plantage. */
    setPercent(file.size > PROGRESS_THRESHOLD ? 0 : null);
    try {
      const media = await cfg.upload(file, { onProgress: setPercent });
      rememberBankMedia(media);
      applyMedia(media, defaultResolutionFor(media, MEDIA_CONTEXTS.web));
      setShowUrlInput(false);
      /* L'upload range le média dans le dossier auto du document côté back
         (`origin: editor`) : il doit apparaître dans la banque sans qu'on
         recharge l'éditeur. */
      invalidateMediaBank();
    } catch (err) {
      setError(err?.message || 'L\'envoi a échoué. Réessayez.');
    } finally {
      setUploading(false);
      setPercent(null);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    await processFile(file);
    if (inputRef.current) inputRef.current.value = '';
  };

  const openPicker = () => {
    if (canUpload && !uploading) inputRef.current?.click();
  };

  const dragProps = {
    onDragOver: (e) => {
      if (!canUpload || uploading) return;
      e.preventDefault();
      setIsDragging(true);
    },
    onDragLeave: (e) => {
      e.preventDefault();
      setIsDragging(false);
    },
    onDrop: async (e) => {
      if (!canUpload || uploading) return;
      e.preventDefault();
      setIsDragging(false);
      await processFile(e.dataTransfer?.files?.[0]);
    },
  };

  const progress = uploading && (
    <Box mt={6}>
      <Progress
        value={percent ?? 100}
        animated={percent == null}
        size="sm"
        radius="xl"
        aria-label="Progression de l'envoi"
      />
      <Text size="xs" c="dimmed" mt={2} role="status">
        {percent == null ? 'Envoi en cours…' : `Envoi en cours — ${percent} %`}
      </Text>
    </Box>
  );

  const urlInput = showUrlInput && (
    <TextInput
      mt={6}
      size="xs"
      value={value || ''}
      onChange={(e) => setUrlByHand(e.currentTarget.value)}
      placeholder={cfg.urlPlaceholder}
      leftSection={<FontAwesomeIcon icon={icon({ name: 'link', style: 'light' })} style={{ fontSize: 14 }} />}
      autoFocus
    />
  );

  const pickLink = (url) => {
    setUrlByHand(url);
    setShowBank(false);
  };

  /* Les adresses de médias vivent souvent déjà dans la banque de liens de
     l'entreprise : « Banque » ouvre les deux, l'onglet décide laquelle. */
  const bankPanel = showBank && canOpenBank && (
    <MediaBankPicker
      onSelect={selectFromBank}
      types={cfg.bankTypes}
      onPickLink={canPickLinks ? pickLink : null}
      linkKind={kind}
    />
  );

  const bankButton = canOpenBank && (
    <Button
      size="compact-xs"
      variant="subtle"
      color="gray"
      leftSection={<FontAwesomeIcon icon={icon({ name: 'images', style: 'light' })} style={{ fontSize: 14 }} />}
      onClick={toggleBank}
    >
      {showBank ? 'Fermer la banque' : 'Banque'}
    </Button>
  );

  /* Seul un appelant qui suit les props voisines peut ranger le triplet de la
     variable à côté de l'URL (`useMediaMetaProps`). */
  const binding = meta?.variable || null;
  const canPickVariable = tracksMeta && kind === 'image' && canPickVariableImage();
  const pickVariable = (variable) => {
    commit({ url: variable.variableValue, ...NO_BANK_MEDIA, variable });
    setError('');
  };
  /* Effacer la variable retire aussi son image, comme effacer le sélecteur
     du classique : l'image ne vient plus de nulle part. */
  const clearVariable = () => commit({ url: '', ...NO_BANK_MEDIA });

  /* Au-dessus de l'image, comme la section Affichage du classique précède sa
     zone de dépôt. */
  const variablePicker = canPickVariable && (
    <VariableImagePicker binding={binding} onPick={pickVariable} onClear={clearVariable} />
  );

  /* Tant que l'admin n'a pas tranché, le switch montre le défaut (lié au CRM
     = dans le formulaire) plutôt qu'un « non » que le formulaire démentirait. */
  const showInFormSwitch = typeof onFormChange === 'function' && kind === 'image'
    && isWebTemplateCatalog() && (
    <Switch
      mb={6}
      size="sm"
      label="Afficher dans le formulaire"
      checked={isVariableBox({ showInForm: form?.showInForm, boundToCrm: !!binding?.variableKey })}
      onChange={(event) => onFormChange({ ...form, showInForm: event.currentTarget.checked })}
    />
  );

  const resolutionField = tracksMeta && value && !binding && (
    <MediaResolutionField
      mediaId={meta?.mediaId}
      resolution={meta?.resolution}
      onChange={changeResolution}
      context={MEDIA_CONTEXTS.web}
    />
  );

  /* Un embed (YouTube, Vimeo) n'est pas un fichier : la balise <video> ne sait
     pas le lire, et l'aperçu du champ montre déjà sa miniature. */
  const canPreview = !!value && !(kind === 'video' && detectEmbedProvider(value));
  const canAdjustFocalPoint = kind === 'image' && typeof onFocalPointChange === 'function';
  const isAdjustingFocalPoint = canAdjustFocalPoint && editingFocalSrc === value;

  return (
    <InspectorRow label={fieldLabel} layout="stacked">
      {variablePicker}
      {showInFormSwitch}

      {value ? (
        /* ── Média présent : aperçu + actions ──────────────────────────── */
        <Box>
          {isAdjustingFocalPoint ? (
            <ImageFocalPointPicker
              src={value}
              value={focalPoint}
              onChange={onFocalPointChange}
              onDone={() => {
                setEditingFocalSrc(null);
                requestAnimationFrame(() => focalButtonRef.current?.focus());
              }}
            />
          ) : (
            <>
              <Box
                {...dragProps}
                style={{
                  position: 'relative',
                  borderRadius: 8,
                  overflow: 'hidden',
                  background: 'var(--mantine-color-gray-1)',
                  outline: isDragging ? '2px dashed var(--mantine-color-editor-5)' : 'none',
                  outlineOffset: -2,
                }}
              >
                <MediaPreview kind={kind} value={value} />
                {canPreview && (
                  /* Une vidéo posée ne se regardait plus : l'aperçu du champ fait
                     110 px, sans contrôles, et le seul endroit où on pouvait la
                     lire était la banque — donc avant de l'avoir choisie. */
                  <Tooltip label="Prévisualiser" withinPortal>
                    <ActionIcon
                      size="sm"
                      variant="filled"
                      color="dark"
                      aria-label={cfg.previewLabel}
                      onClick={() => setShowPreview(true)}
                      style={{ position: 'absolute', top: 6, left: 6, opacity: 0.75 }}
                    >
                      <FontAwesomeIcon
                        icon={icon({ name: 'eye', style: 'light' })}
                        style={{ fontSize: 13 }}
                      />
                    </ActionIcon>
                  </Tooltip>
                )}
                <ActionIcon
                  size="sm"
                  variant="filled"
                  color="red"
                  aria-label={cfg.removeLabel}
                  onClick={() => {
                    setUrlByHand('');
                    setShowUrlInput(false);
                    setError('');
                  }}
                  style={{ position: 'absolute', top: 6, right: 6 }}
                >
                  <FontAwesomeIcon
                    icon={icon({ name: 'xmark', style: 'light' })}
                    style={{ fontSize: 13 }}
                  />
                </ActionIcon>
              </Box>

              {/* Jusqu'à trois actions (Remplacer, Banque, lien) : elles passent
                  à la ligne plutôt que d'être tronquées dans l'inspecteur. */}
              <Group mt={6} gap={6} wrap="wrap" justify="space-between">
                <Button
                  size="compact-xs"
                  variant="light"
                  leftSection={<FontAwesomeIcon icon={icon({ name: 'arrows-rotate', style: 'light' })} style={{ fontSize: 14 }} />}
                  onClick={openPicker}
                  loading={uploading}
                  disabled={!canUpload}
                >
                  Remplacer
                </Button>
                {bankButton}
                <Button
                  size="compact-xs"
                  variant="subtle"
                  leftSection={<FontAwesomeIcon icon={icon({ name: 'link', style: 'light' })} style={{ fontSize: 14 }} />}
                  onClick={toggleUrlInput}
                >
                  {showUrlInput ? 'Cacher le lien' : 'Voir le lien'}
                </Button>
              </Group>
              {canAdjustFocalPoint && (
                <Button
                  ref={focalButtonRef}
                  mt="sm"
                  size="xs"
                  variant="default"
                  fullWidth
                  leftSection={<FontAwesomeIcon icon={icon({ name: 'crosshairs', style: 'light' })} />}
                  onClick={() => {
                    setShowBank(false);
                    setShowUrlInput(false);
                    setEditingFocalSrc(value);
                  }}
                >
                  Ajuster le cadrage
                </Button>
              )}
              {progress}
              {resolutionField}
              {urlInput}
              {bankPanel}
            </>
          )}
        </Box>
      ) : (
        /* ── Pas de média : une seule surface d'action ─────────────────── */
        <Box>
          <Box
            role="button"
            tabIndex={0}
            aria-label={cfg.dropAria}
            onClick={openPicker}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openPicker(); }}
            {...dragProps}
            p="md"
            style={{
              border: `1.5px dashed ${isDragging ? 'var(--mantine-color-editor-5)' : 'var(--mantine-color-gray-4)'}`,
              borderRadius: 8,
              background: isDragging ? 'var(--mantine-color-editor-0)' : 'var(--mantine-color-gray-0)',
              transition: 'border-color 0.12s ease, background 0.12s ease',
              textAlign: 'center',
              cursor: canUpload ? 'pointer' : 'not-allowed',
              opacity: canUpload ? 1 : 0.6,
            }}
          >
            <FontAwesomeIcon
              icon={cfg.icon}
              style={{
                fontSize: 20,
                color: isDragging ? 'var(--mantine-color-editor-7)' : 'var(--mantine-color-gray-6)',
              }}
            />
            <Text size="xs" fw={500} c={isDragging ? 'editor.7' : 'gray.7'} mt={2}>
              {cfg.addLabel}
            </Text>
            <Text size="xs" c="dimmed">
              {cfg.hint}
            </Text>
          </Box>
          {progress}

          {!canUpload && (
            <Text size="xs" c="dimmed" mt={4}>
              Sauvegardez d&apos;abord pour activer l&apos;envoi.
            </Text>
          )}

          <Group mt={4} gap={6} wrap="wrap">
            {bankButton}
            <Button
              size="compact-xs"
              variant="subtle"
              color="gray"
              leftSection={<FontAwesomeIcon icon={icon({ name: 'link', style: 'light' })} style={{ fontSize: 14 }} />}
              onClick={toggleUrlInput}
            >
              Utiliser un lien
            </Button>
          </Group>
          {urlInput}
          {bankPanel}
        </Box>
      )}

      {error && (
        <Text size="xs" c="red" mt={4} role="alert">{error}</Text>
      )}

      <MediaPreviewModal
        opened={showPreview}
        onClose={() => setShowPreview(false)}
        kind={kind}
        src={value}
        title={fieldLabel}
      />

      <input
        ref={inputRef}
        type="file"
        accept={cfg.accept}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </InspectorRow>
  );
};

export default MediaUploadField;
